import AssessmentIcon from '@mui/icons-material/Assessment';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Divider,
  Drawer,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Toolbar,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppSoilingAssessment from '../components/app-assessment/AppSoilingAssessment';
import AppSoilingMitigationStrategy from '../components/app-optimization/AppSoilingMitigationStrategy.tsx';
import AppSoilingOperationalStrategy from '../components/app-optimization/AppSoilingOperationalStrategy';
import FormRenderer from '../components/form-engine/FormRenderer';
import type { PageSchema } from '../components/form-engine/types';
import ReportActions from '../components/reports/ReportActions';
import { RAIN_RECOMMENDATION_FIELDS, type RainRecommendationKey } from '../constants/rainRecommendation';
import { Apps, useAppContext } from '../contexts/AppContext';
import { useFormContext } from '../contexts/FormContext';
import { useFormData } from '../hooks/useFormValues';
import { useProjectMutations } from '../hooks/useProjectMutations';
import { useProjectValidation } from '../hooks/useProjectValidation';
import { hasCoordinates, rainRecommendationQueryOptions, useRainRecommendation } from '../hooks/useRainRecommendation';
import { useSensorData } from '../hooks/useSensorData';
import { Server } from '../services/Server';
import type { Project, RainRecommendation } from '../types';
import { handleError } from '../utils/Error';
import { getSoilingBlockKeys } from '../utils/soilingSensor';
import { generatePages } from './Pages';

type ProjectWithResults = Project & { results?: Record<string, unknown> | null };

const ROLE_SECTIONS: { role: string; section: string; label: string; icon: React.ReactNode }[] = [
  { role: 'assessment', section: 'soiling_risk_assessment', label: 'Soiling Assessment', icon: <AssessmentIcon /> },
  { role: 'mitigation_strategy', section: 'mitigation_strategy', label: 'Mitigation Strategy', icon: <CompareArrowsIcon /> },
  { role: 'ex_post_analysis', section: 'ex_post_analysis', label: 'Ex-Post Analysis', icon: <CompareArrowsIcon /> },
  { role: 'operational_strategy', section: 'operational_strategy', label: 'Operational Strategy', icon: <CompareArrowsIcon /> },
  { role: 'schedule_optimization', section: 'schedule_optimization', label: 'Schedule Optimization', icon: <CompareArrowsIcon /> },
];

// Fills only the empty leaves of `data` from `defaults`: a saved block (e.g. `rain` saved with just rain_efficiency)
// keeps its values but still gets the defaults of the keys it lacks.
const fillMissingDefaults = (data: Record<string, unknown>, defaults: Record<string, unknown>) => {
  const isEmpty = (value: unknown) => value === undefined || value === null || value === '';
  const isBlock = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

  Object.entries(defaults).forEach(([key, defaultValue]) => {
    const current = data[key];
    if (isBlock(defaultValue) && isBlock(current)) {
      data[key] = fillMissingDefaults({ ...current }, defaultValue);
    } else if (isEmpty(current)) {
      data[key] = defaultValue;
    }
  });
  return data;
};

const Project = () => {
  const { role, completed, setCompleted, activeStep, setActiveStep, referenceProjects, setApp, modified, setModified } = useAppContext();
  const { setFormData, getFormDataItem, updateDataValue, setResults, getValues, handleChange } = useFormContext();
  const formData = useFormData();
  const { getProjectAPI, getCleaningPackagesAPI, getRainRecommendationAPI } = Server();
  const queryClient = useQueryClient();
  const [sectionName, setSectionName] = useState('location');
  const [pages, setPages] = useState<PageSchema[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const lastCompletedStateRef = useRef<string>('');
  const creatingProjectRef = useRef(false);
  const mapRef = useRef<React.ComponentProps<typeof FormRenderer>['mapRef']['current']>(null);
  const params = useParams();
  const navigate = useNavigate();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const { data: cleaningPackages = {}, isSuccess: isCleaningPackagesLoaded } = useQuery({
    queryKey: ['cleaning_packages'],
    queryFn: () => getCleaningPackagesAPI(),
    enabled: role === 'mitigation_strategy' || role === 'assessment' || role === 'operational_strategy',
  });

  const {
    data: project = undefined,
    isError: isProjectError,
    error: projectError,
  } = useQuery({
    queryKey: ['project', params?.id],
    queryFn: () => getProjectAPI(params?.id as string),
    enabled: !!params?.id,
  });

  // A project that does not belong to the current user (or an orphan legacy project) now returns 404.
  // Surface a message and redirect to the projects list instead of rendering an empty/misleading form.
  useEffect(() => {
    if (isProjectError) {
      handleError(projectError);
      navigate('/projects', { replace: true });
    }
  }, [isProjectError, projectError, navigate]);

  // Another user's project: everything is readable, but only a copy can be edited.
  const isReadOnly = project?.can_edit === false;

  const { checkCompleted, isFormValid, isValidForm, validateFields } = useProjectValidation(pages);
  const { isLoadingSensorData } = useSensorData({ sectionName, projectId: params?.id });
  const { isSaving, preservedPatternData, handleSave, handleDuplicate } = useProjectMutations({
    projectId: params?.id,
    project,
    isValidForm,
  });

  const { data: rainRecommendation } = useRainRecommendation();
  const appliedRainDefaultsRef = useRef<Record<string, number | null>>({});

  // Climate-based defaults: fill empty fields and replace earlier defaults, never user-entered or saved values.
  const applyRainDefaults = (recommendation: RainRecommendation) => {
    (Object.keys(RAIN_RECOMMENDATION_FIELDS) as RainRecommendationKey[]).forEach((key) => {
      const value = recommendation[key]?.value ?? null;
      RAIN_RECOMMENDATION_FIELDS[key].forEach((name) => {
        const current = getFormDataItem(getValues(), name);
        const isEmpty = current === '' || current === null || current === undefined;
        const isPreviousDefault = !isEmpty && current === appliedRainDefaultsRef.current[name];
        if (isEmpty ? value === null : !isPreviousDefault || current === value) {
          return;
        }
        appliedRainDefaultsRef.current[name] = value;
        handleChange({ target: { name, value: (value ?? '') as string, type: 'number' } }, {});
      });
    });
  };

  useEffect(() => {
    if (rainRecommendation) {
      applyRainDefaults(rainRecommendation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rainRecommendation]);

  // Applies the rain defaults before saving: a location change saves while its recommendation is still loading,
  // and a default filled after that save starts would never be persisted.
  const saveProject = async () => {
    const { latitude, longitude }: { latitude?: unknown; longitude?: unknown } = getFormDataItem(getValues(), 'location') || {};
    if (hasCoordinates(latitude, longitude)) {
      try {
        const recommendation = await queryClient.fetchQuery(rainRecommendationQueryOptions(latitude, longitude, getRainRecommendationAPI));
        applyRainDefaults(recommendation);
      } catch (error) {
        handleError(error);
      }
    }
    handleSave();
  };

  const handleComplete = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
  };

  useEffect(() => {
    setApp(Apps.SoilingMitigation);
    setActiveStep(0);
  }, []);

  useEffect(() => {
    setSectionName('location');
  }, [role]);

  // Leaving a section: re-enable any "external source" toggle (*.from_api) that was turned off but
  // never received a file, so the project never sits in an invalid (disabled + no pattern) state.
  const prevActiveStepRef = useRef(activeStep);
  useEffect(() => {
    if (prevActiveStepRef.current === activeStep) {
      return;
    }
    prevActiveStepRef.current = activeStep;

    let changed = false;
    const next: Record<string, unknown> = { ...formData };
    Object.keys(next).forEach((key) => {
      const block = next[key] as Record<string, unknown> | null;
      if (block && typeof block === 'object' && block.from_api === false) {
        const pattern = block.rain_pattern ?? block.pattern;
        const hasValidPattern = pattern && typeof pattern !== 'string';
        if (!hasValidPattern) {
          next[key] = { ...block, from_api: true };
          changed = true;
        }
      }
    });
    if (changed) {
      setFormData(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  useEffect(() => {
    setPages((generatePages(referenceProjects, cleaningPackages, params?.id) as PageSchema[]).filter((step) => step.roles.includes(role)));
  }, [role, isCleaningPackagesLoaded, params?.id]);

  useEffect(() => {
    if (validateFields(pages[activeStep])) {
      const completedStateKey = `${activeStep}-${completed[activeStep]}`;
      if (lastCompletedStateRef.current !== completedStateKey) {
        handleComplete();
        lastCompletedStateRef.current = completedStateKey;
      }

      if (
        mapRef.current &&
        formData?.location?.latitude &&
        formData?.location?.longitude &&
        !isNaN(Number(formData.location.latitude)) &&
        !isNaN(Number(formData.location.longitude))
      ) {
        mapRef.current.setView([Number(formData.location.latitude), Number(formData.location.longitude)]);
      }
    }
  }, [formData, activeStep, pages]);

  useEffect(() => {
    const defaultFormData = {};
    if (pages && pages.length > 0) {
      pages.forEach((page) => {
        const defaultValues = page.default_values;
        if (defaultValues) {
          Object.keys(defaultValues).forEach((key) => {
            const existingValue = getFormDataItem(formData, key);
            if (existingValue === undefined || existingValue === null || existingValue === '') {
              updateDataValue(defaultFormData, key, defaultValues[key]);
            }
          });
        }
      });
    }

    if (!project && isCleaningPackagesLoaded && cleaningPackages && Object.keys(cleaningPackages).length > 0) {
      if (!formData.cleaning_packages) {
        updateDataValue(defaultFormData, 'cleaning_packages', cleaningPackages);
      }
    }

    if (project || Object.keys(defaultFormData).length > 0) {
      const mergedData = fillMissingDefaults({ ...formData, ...defaultFormData, ...structuredClone(project) }, defaultFormData);
      const transformedData = transformSoilingSensorDataFromArray(mergedData);

      const finalData = { ...transformedData };

      const transformedSoilingKeys = getSoilingBlockKeys(transformedData);

      transformedSoilingKeys.forEach((transformedKey) => {
        let patternToRestore = null;

        if (preservedPatternData[transformedKey]) {
          patternToRestore = preservedPatternData[transformedKey];
        } else {
          const transformedFileUid = getFormDataItem(transformedData, `${transformedKey}.file_uid`);
          if (transformedFileUid) {
            Object.keys(preservedPatternData).forEach((preservedKey) => {
              const preservedFileUid = getFormDataItem(formData, `${preservedKey}.file_uid`);
              if (preservedFileUid === transformedFileUid) {
                patternToRestore = preservedPatternData[preservedKey];
              }
            });
          }
        }

        if (patternToRestore) {
          updateDataValue(finalData, `${transformedKey}.pattern`, patternToRestore);
        }
      });

      setFormData(finalData);
    } else if (!formData || Object.keys(formData).length === 0) {
      setFormData({});
    }

    if (modified) {
      setModified(false);
    }
    setResults((project as ProjectWithResults | undefined)?.results as Record<string, unknown>);

    document.title = 'Project | ' + (project?.name ? project?.name : 'Assessment');
  }, [project, pages, cleaningPackages, isCleaningPackagesLoaded]);

  const transformSoilingSensorDataFromArray = (data: Record<string, unknown> | null | undefined) => {
    if (!data || !data.soiling_sensor_data || !Array.isArray(data.soiling_sensor_data)) {
      return data;
    }

    const transformedData = { ...data };
    const soilingSensorArray = transformedData.soiling_sensor_data as {
      file_uid?: unknown;
      _order?: unknown;
      name?: unknown;
      type?: unknown;
    }[];

    delete transformedData.soiling_sensor_data;

    getSoilingBlockKeys(transformedData).forEach((key) => {
      delete transformedData[key];
    });

    soilingSensorArray.forEach((item, index: number) => {
      const blockKey = index === 0 ? 'soiling_sensor_data' : `soiling_sensor_data_${index + 1}`;
      transformedData[blockKey] = {
        file_uid: item.file_uid,
        _order: item._order || index + 1,
        name: item.name || '',
        type: item.type || 'rde300',
      };
    });

    return transformedData;
  };

  React.useEffect(() => {
    if (modified && params?.id && !isReadOnly) {
      saveProject();
    }
  }, [modified]);

  // A new project is created automatically as soon as its form is valid, so no save button is needed.
  // The ref blocks a second POST while the first one (or its rain-recommendation fetch) is still pending.
  useEffect(() => {
    if (params?.id) {
      return;
    }
    if (!modified) {
      creatingProjectRef.current = false;
      return;
    }
    if (creatingProjectRef.current || !isFormValid()) {
      return;
    }
    creatingProjectRef.current = true;
    saveProject();
  }, [modified, formData]);

  const drawerWidth = 240;

  const hasLocation = !!(formData?.location?.latitude && formData?.location?.longitude);

  const sectionResults: { section: string; ready: boolean; content: React.ReactNode; fallback: string }[] = [
    {
      section: 'soiling_risk_assessment',
      ready: !!(hasLocation && formData.surface_tilt != undefined && formData.cleaning_efficiency),
      content: <AppSoilingAssessment name={''} projectId={project?.id as string} setResults={setResults} />,
      fallback: 'Missing required fields.',
    },
    {
      section: 'mitigation_strategy',
      ready: hasLocation,
      content: <AppSoilingMitigationStrategy projectId={project?.id} setResults={setResults} />,
      fallback: 'Please choose a location to see results.',
    },
    {
      section: 'operational_strategy',
      ready: hasLocation,
      content: <AppSoilingOperationalStrategy setResults={setResults} project_id={params?.id as string} />,
      fallback: 'Please choose a location to see results.',
    },
    {
      section: 'ex_post_analysis',
      ready: hasLocation,
      content: <AppSoilingOperationalStrategy type={'ex-post'} setResults={setResults} project_id={params?.id as string} />,
      fallback: 'Please choose a location to see results.',
    },
    {
      section: 'schedule_optimization',
      ready: hasLocation,
      content: <AppSoilingOperationalStrategy type={'schedule-optimization'} setResults={setResults} project_id={params?.id as string} />,
      fallback: 'Please choose a location to see results.',
    },
  ];

  return (
    <Box sx={{ margin: 0, height: '100vh' }}>
      <Toolbar />
      <Box sx={{ display: 'flex' }}>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Toolbar />
          <Toolbar>
            <Grid container>
              <Grid sx={{ width: '100%', mt: 2 }}>
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  aria-owns={open ? 'mouse-over-popover' : undefined}
                  aria-haspopup="true"
                  onMouseEnter={handlePopoverOpen}
                  onMouseLeave={handlePopoverClose}
                >
                  {formData?.name || 'Plant Name'}
                </Typography>
                {formData?.name && (
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: 'none' }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>{formData?.name}</Typography>
                  </Popover>
                )}
              </Grid>
              <Grid>
                {project && (
                  <IconButton aria-label="duplicate" onClick={handleDuplicate} loading={isSaving}>
                    <ContentCopyIcon sx={{ color: 'none' }} />
                  </IconButton>
                )}
                {project?.id && <ReportActions projectId={project.id} hasUnsavedChanges={modified || isSaving} readOnly={isReadOnly} />}
              </Grid>
            </Grid>
          </Toolbar>
          <Divider />
          <List>
            {pages?.map((page, index) => {
              if (page.roles.includes(role)) {
                return page.divider ? (
                  <Divider />
                ) : (
                  <ListItem key={page.name} disablePadding>
                    <ListItemButton
                      sx={{
                        borderLeft: sectionName === page.section ? '5px solid #007acd' : '5px solid rgba(0, 0, 0, 0)',
                      }}
                      selected={sectionName === page.section}
                      onClick={() => {
                        if (isValidForm()) {
                          setActiveStep(index);
                          setSectionName(page.section);
                          page.onClick?.();
                        }
                      }}
                      disabled={
                        (Object.prototype.hasOwnProperty.call(page, 'disabled') && page.disabled) || checkCompleted(page.prerequisites)
                      }
                    >
                      <ListItemIcon>{page.icon}</ListItemIcon>
                      <ListItemText primary={page.name} />
                    </ListItemButton>
                  </ListItem>
                );
              }
            })}
            <>
              <Divider />
              {ROLE_SECTIONS.filter((item) => item.role === role).map((item) => (
                <ListItem key={item.section} disablePadding>
                  <ListItemButton
                    sx={{ borderLeft: sectionName === item.section ? '5px solid #007acd' : '' }}
                    selected={sectionName === item.section}
                    disabled={!project}
                    onClick={() => {
                      setActiveStep(-1);
                      setSectionName(item.section);
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            width: { xs: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          {isReadOnly ? (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleDuplicate} disabled={isSaving}>
                  Copy project
                </Button>
              }
            >
              This project belongs to another user and is read-only. Copy it to get your own editable version.
            </Alert>
          ) : null}
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', p: 1 }} noValidate>
            {/* `inert` (not `fieldset disabled`, which ignores MUI selects/autocompletes) blocks every interaction. */}
            <Box
              component="fieldset"
              {...(isReadOnly ? ({ inert: '' } as object) : {})}
              sx={{ border: 0, p: 0, m: 0, minWidth: 0, opacity: isReadOnly ? 0.85 : 1 }}
            >
              <Grid container spacing={2}>
                {pages[activeStep]?.description ? (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    {typeof pages[activeStep]?.description === 'object' && pages[activeStep]?.description !== null
                      ? (pages[activeStep]?.description as Record<string, React.ReactNode>)[role] ||
                        (pages[activeStep]?.description as React.ReactNode)
                      : (pages[activeStep]?.description as React.ReactNode)}
                  </Box>
                ) : null}
                <FormRenderer fields={pages[activeStep]?.fields ?? []} mapRef={mapRef} isLoadingSensorData={isLoadingSensorData} />
              </Grid>
            </Box>
          </Box>
          {sectionResults
            .filter((s) => s.section === sectionName)
            .map((s) => (
              <Box key={s.section} sx={{ mt: 0 }}>
                {s.ready ? s.content : <Typography color="text.primary">{s.fallback}</Typography>}
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Project;
